import React, { useState } from "react";
import api from "@/lib/api";
import { MessageSquare, Send, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/utils";

const ROLE_BADGE = { admin: "Admin", coach: "Coach", parent: "Parent", player: "Joueur" };

export const CommentsThread = ({ targetType, targetId, count = 0 }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [n, setN] = useState(count);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/comments?target_type=${targetType}&target_id=${targetId}`);
      setItems(data); setN(data.length);
    } catch { toast.error("Chargement des commentaires impossible"); }
    finally { setLoading(false); }
  };
  const toggle = () => { const nx = !open; setOpen(nx); if (nx) load(); };

  const post = async () => {
    const t = text.trim(); if (!t) return;
    setPosting(true);
    try { await api.post("/comments", { target_type: targetType, target_id: targetId, text: t }); setText(""); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setPosting(false); }
  };
  const saveEdit = async (id) => {
    const t = editText.trim(); if (!t) return;
    try { await api.put(`/comments/${id}`, { text: t }); setEditId(null); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce commentaire ?")) return;
    try { await api.delete(`/comments/${id}`); await load(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-border/60">
      <button data-testid={`comments-toggle-${targetType}-${targetId}`} onClick={toggle}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground hover:text-primary transition-colors">
        <MessageSquare size={13} /> Commentaires{n ? ` (${n})` : ""}
      </button>
      {open && (
        <div className="mt-3 space-y-3" data-testid={`comments-panel-${targetType}-${targetId}`}>
          {loading ? <p className="text-xs text-muted-foreground">Chargement…</p> : items.length === 0 ? (
            <p className="text-xs text-muted-foreground">Aucun commentaire pour le moment. Soyez le premier à réagir !</p>
          ) : items.map((c) => (
            <div key={c.id} data-testid="comment-item" className="rounded-lg bg-secondary/40 p-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold flex items-center gap-1.5">
                  {c.author_name}
                  {ROLE_BADGE[c.author_role] && <span className="rounded-full bg-primary/15 text-primary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide">{ROLE_BADGE[c.author_role]}</span>}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] text-muted-foreground">{fmtDateTime(c.created_at)}{c.edited ? " · modifié" : ""}</span>
                  {c.can_edit && editId !== c.id && (
                    <>
                      <button data-testid={`comment-edit-${c.id}`} onClick={() => { setEditId(c.id); setEditText(c.text); }} className="text-muted-foreground hover:text-primary"><Pencil size={12} /></button>
                      <button data-testid={`comment-delete-${c.id}`} onClick={() => remove(c.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={12} /></button>
                    </>
                  )}
                </div>
              </div>
              {editId === c.id ? (
                <div className="mt-1.5 flex items-end gap-2">
                  <textarea data-testid={`comment-edit-input-${c.id}`} value={editText} onChange={(e) => setEditText(e.target.value)} rows={2}
                    className="flex-1 rounded-lg bg-background border border-border p-2 text-sm outline-none focus:border-primary resize-y" />
                  <button data-testid={`comment-edit-save-${c.id}`} onClick={() => saveEdit(c.id)} className="p-2 rounded-lg bg-primary text-primary-foreground"><Check size={14} /></button>
                  <button onClick={() => setEditId(null)} className="p-2 rounded-lg bg-secondary"><X size={14} /></button>
                </div>
              ) : (
                <p className="text-sm mt-1 whitespace-pre-line break-words">{c.text}</p>
              )}
            </div>
          ))}
          <div className="flex items-end gap-2">
            <textarea data-testid={`comment-input-${targetType}-${targetId}`} value={text} onChange={(e) => setText(e.target.value)} rows={2}
              placeholder="Écrire un commentaire…" className="flex-1 rounded-lg bg-secondary/40 border border-border p-2 text-sm outline-none focus:border-primary resize-y" />
            <button data-testid={`comment-post-${targetType}-${targetId}`} onClick={post} disabled={posting || !text.trim()}
              className="p-2.5 rounded-lg bg-primary text-primary-foreground disabled:opacity-50"><Send size={15} /></button>
          </div>
        </div>
      )}
    </div>
  );
};
